<template>
  <div
    class="form-field"
    :class="ofType"
  >
    <UiComponentLabel :id="id">
      <slot />
      <span v-if="ofType !== 'checkbox' && required">*</span>
    </UiComponentLabel>
    <Component
      :is="is"
      ref="$field"
      v-bind="properties"
      @input:change="emit('field:change', $event)"
      @select:change="emit('field:change', $event)"
    />
  </div>
</template>

<script setup>
// Emitter
const emit = defineEmits([
  'field:change',
])

// Props
const props = defineProps({
  checked: {
    default() {
      return false
    },
    required: false,
    type: Boolean,
  },
  errorMessage: {
    default() {
      return undefined
    },
    required: false,
    type: String,
  },
  id: {
    required: true,
    type: String,
  },
  max: {
    default() {
      return undefined
    },
    required: false,
    type: Number,
  },
  min: {
    default() {
      return undefined
    },
    required: false,
    type: Number,
  },
  name: {
    required: true,
    type: String,
  },
  placeholder: {
    default() {
      return ''
    },
    required: false,
    type: String,
  },
  ofType: {
    default() {
      return 'input'
    },
    required: false,
    type: String,
    validator(value) {
      return (
        ['checkbox', 'input', 'select', 'textarea'].indexOf(value) !== -1
      )
    },
  },
  options: {
    default() {
      return []
    },
    required: false,
    type: Array,
  },
  required: {
    default() {
      return false
    },
    required: false,
    type: Boolean,
  },
  size: {
    default() {
      return 'm'
    },
    required: false,
    type: String,
  },
  type: {
    default() {
      return 'text'
    },
    required: false,
    type: String,
  },
})

// Refs
const $field = ref(null)

// Computed Properties
const is = computed(() => {
  switch (props.ofType) {
    case 'checkbox':
      return resolveComponent('UiComponentCheckbox')
    case 'select':
      return resolveComponent('UiComponentSelect')
    case 'textarea':
      return resolveComponent('UiComponentTextarea')
    default:
      return resolveComponent('UiComponentInput')
  }
})

const properties = computed(() => {
  switch (props.ofType) {
    case 'checkbox':
      return {
        id: props.id,
        checked: props.checked,
      }
    default:
      return props
  }
})

const value = computed(() => {
  return $field.value.$el.value
})

defineExpose({
  id: props.id,
  $field,
})
</script>

<style lang="postcss" scoped>
.form-field {
  @apply flex w-full;

  &.checkbox {
    @apply flex-row items-start;

    input {
      @apply flex-shrink-0;
    }

    label {
      @apply order-1 ml-3 normal-case text-grey-blue-600;
      margin-top: -2px;
    }

    :deep(label a) {
      @apply text-livestorm-blue-700;
    }
  }

  &.input,
  &.select,
  &.textarea {
    @apply flex-col;

    label {
      @apply font-semibold text-grey-blue-900 uppercase;
    }

    label + * {
      @apply mt-1;
    }
  }
}
</style>
